import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  Spread,
} from 'lexical';
import { $applyNodeReplacement, TextNode } from 'lexical';
import { addClassNamesToElement } from '@lexical/utils';

/**
 * Data structure for text entity metadata
 */
export interface TextEntityData {
  title?: string;
  description?: string;
  id?: string;
  [key: string]: unknown; // Allow additional properties
}

export type SerializedPhxTextEntityNode = Spread<
  {
    textEntityName: string;
    type: 'textEntity';
    data: TextEntityData;
    version: 1;
  },
  SerializedTextNode
>;

function convertPhxTextEntityElement(domNode: HTMLElement): DOMConversionOutput | null {
  const textContent = domNode.textContent;
  if (textContent === null || textContent.trim() === '') {
    return null; // Don't create empty entities
  }

  try {
    const node = $createTextEntityNode(textContent);

    // Restore data attribute
    if (domNode.hasAttribute('data-textentity-data')) {
      try {
        node.__data = JSON.parse(domNode.getAttribute('data-textentity-data') ?? '{}');
      } catch {
        // Silently ignore parse errors
      }
    }

    // Use Lexical's native formatting detection approach
    // Since PhxTextEntityNode extends TextNode, we can use the same format detection
    // that Lexical's TextNode uses during DOM import. We check:
    // 1. Custom PhxEditorTheme classes
    // 2. Parent semantic elements (strong, em, u, s, etc.) - Lexical's native approach
    // 3. Inline styles - Lexical's native approach

    const classList = domNode.classList;
    const parent = domNode.parentElement;
    const style = domNode.style;

    // Check custom format classes
    if (classList.contains('PhxEditorTheme__textBold')) {
      node.toggleFormat('bold');
    }
    if (classList.contains('PhxEditorTheme__textItalic')) {
      node.toggleFormat('italic');
    }
    if (classList.contains('PhxEditorTheme__textUnderline')) {
      node.toggleFormat('underline');
    }
    if (classList.contains('PhxEditorTheme__textStrikethrough')) {
      node.toggleFormat('strikethrough');
    }

    // Check parent semantic elements (Lexical's native TextNode importDOM approach)
    if (parent) {
      const tagName = parent.tagName.toLowerCase();
      if (tagName === 'strong' || tagName === 'b') {
        node.toggleFormat('bold');
      }
      if (tagName === 'em' || tagName === 'i') {
        node.toggleFormat('italic');
      }
      if (tagName === 'u') {
        node.toggleFormat('underline');
      }
      if (tagName === 's' || tagName === 'strike' || tagName === 'del') {
        node.toggleFormat('strikethrough');
      }
    }

    // Check inline styles (Lexical's native TextNode importDOM approach)
    if (style.fontWeight === 'bold' || style.fontWeight === '700') {
      node.toggleFormat('bold');
    }
    if (style.fontStyle === 'italic') {
      node.toggleFormat('italic');
    }
    if (style.textDecoration?.includes('underline')) {
      node.toggleFormat('underline');
    }
    if (style.textDecoration?.includes('line-through')) {
      node.toggleFormat('strikethrough');
    }

    return { node };
  } catch (error) {
    console.warn('Failed to convert text entity element:', error);
    return null; // Graceful degradation
  }
}

/**
 * PhxTextEntityNode represents a reference to a text entity (e.g., section reference, policy reference).
 * It extends TextNode to support custom data storage and format preservation.
 *
 * Text entities are inserted via modal dialogs and cannot be edited directly by users.
 * They can only be deleted, not modified.
 *
 * @example
 * ```ts
 * const node = $createTextEntityNode('Section 1.2', {
 *   title: 'Section 1.2: Policy Overview',
 *   id: 'section-1-2'
 * });
 * ```
 */
export class PhxTextEntityNode extends TextNode {
  __textEntity: string;
  __data?: TextEntityData;

  static getType(): string {
    return 'textEntity';
  }

  static clone(node: PhxTextEntityNode): PhxTextEntityNode {
    return new PhxTextEntityNode(node.__textEntity, node.__text, node.__key, node.__data);
  }

  static importJSON(serializedNode: SerializedPhxTextEntityNode): PhxTextEntityNode {
    const node = $createTextEntityNode(serializedNode.textEntityName);
    node.__data = serializedNode.data;
    return node.updateFromJSON(serializedNode);
  }

  constructor(textEntityName: string, text?: string, key?: NodeKey, data?: TextEntityData) {
    // The `text` you pass to super(...) is what shows in the editor
    super(text ?? textEntityName, key);
    this.__textEntity = textEntityName;
    this.__data = data;
  }

  exportJSON(): SerializedPhxTextEntityNode {
    return {
      ...super.exportJSON(),
      textEntityName: this.__textEntity,
      type: 'textEntity',
      data: this.__data ?? {},
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    addClassNamesToElement(element, config.theme.textEntity || 'text-entity');
    element.setAttribute('data-lexical-text', 'true');
    return element;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');

    // Build class list with format classes
    const classes = ['text-entity'];
    if (this.hasFormat('bold')) classes.push('PhxEditorTheme__textBold');
    if (this.hasFormat('italic')) classes.push('PhxEditorTheme__textItalic');
    if (this.hasFormat('underline')) classes.push('PhxEditorTheme__textUnderline');
    if (this.hasFormat('strikethrough')) classes.push('PhxEditorTheme__textStrikethrough');

    addClassNamesToElement(element, ...classes);

    // Set data attribute
    if (this.__data) {
      element.setAttribute('data-textentity-data', JSON.stringify(this.__data));
    }

    element.textContent = this.__text;
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.classList.contains('text-entity')) {
          return null;
        }
        return {
          conversion: convertPhxTextEntityElement,
          priority: 1,
        };
      },
    };
  }

  isTextEntity(): true {
    return true;
  }

  /**
   * Prevents text insertion before the entity.
   * This ensures text entities remain atomic units and prevents formatting issues.
   */
  canInsertTextBefore(): boolean {
    return false;
  }

  /**
   * Prevents text insertion after the entity.
   * This ensures text entities remain atomic units and prevents formatting issues.
   */
  canInsertTextAfter(): boolean {
    return false;
  }

  /**
   * Override setTextContent to prevent editing the text content.
   * Text entities can only be deleted, not modified.
   */
  setTextContent(text: string): this {
    // Do not allow text content to be changed
    return this;
  }
}

/**
 * Creates a PhxTextEntityNode with the given label and optional data.
 *
 * @param label - The display text for the entity (e.g., "Section 1.2")
 * @param data - Optional metadata (title, description, id, etc.)
 * @returns A new PhxTextEntityNode instance
 *
 * @example
 * ```ts
 * const entity = $createTextEntityNode('Section 1.2', {
 *   title: 'Section 1.2: Policy Overview',
 *   id: 'section-1-2'
 * });
 * ```
 */
export function $createTextEntityNode(label: string, data?: TextEntityData): PhxTextEntityNode {
  const node = new PhxTextEntityNode(label, label, undefined, data);
  node.setMode('segmented').toggleDirectionless();
  return $applyNodeReplacement(node);
}

export function $isTextEntityNode(node: LexicalNode | null | undefined): node is PhxTextEntityNode {
  return node instanceof PhxTextEntityNode;
}
